import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import TuneRounded from "@mui/icons-material/TuneRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import type { Settings } from "@ebook/contracts";
import { api } from "../lib/api-client";
import { PageError, PageLoading } from "../components/PageState";
import { GlobalInput } from "../components/GlobalInput";
import { SectionHeader } from "../components/SectionHeader";
import { useTranslate } from "../i18n";
import { useLocaleStore } from "../stores/locale-store";

const modelDescriptions: Record<Settings["openAiModel"], string> = {
  "gpt-5.4-mini": "แนะนำ — คุณภาพดีและประหยัดสำหรับตรวจข้อความ",
  "gpt-5.6-luna": "เร็ว เหมาะกับเอกสารจำนวนมาก",
  "gpt-5.6-terra": "คุณภาพสูงขึ้น เหมาะกับข้อความที่แก้ยาก",
  "gpt-4o-mini": "ประหยัดที่สุด เหมาะกับการแก้คำทั่วไป",
};

type PersistedSettings = Pick<Settings, "locale" | "theme" | "openAiModel">;

function persistedSettings(settings: Settings): PersistedSettings {
  return {
    locale: settings.locale,
    theme: settings.theme,
    openAiModel: settings.openAiModel,
  };
}

export function SettingsPage() {
  const __ = useTranslate();
  const { locale, setLocale } = useLocaleStore();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const settingsRef = useRef<Settings | null>(null);
  const apiKeyRef = useRef("");
  const savedSettingsRef = useRef<PersistedSettings | null>(null);
  const saveQueueRef = useRef(Promise.resolve());
  const pendingSaveCountRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const loaded = await api.settings();
      settingsRef.current = loaded;
      savedSettingsRef.current = persistedSettings(loaded);
      setSettings(loaded);
      setLocale(loaded.locale);
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("โหลดการตั้งค่าไม่สำเร็จ"));
    }
  }, [__, setLocale]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    setSettings((current) => {
      if (!current || current.locale === locale) return current;
      const next = { ...current, locale };
      settingsRef.current = next;
      return next;
    });
  }, [locale]);

  function setDraftSettings(nextSettings: Settings) {
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
  }

  function setDraftApiKey(nextApiKey: string) {
    apiKeyRef.current = nextApiKey;
    setApiKey(nextApiKey);
  }

  function hasUnsavedChanges(nextSettings: Settings, nextApiKey: string, clearOpenAiApiKey: boolean) {
    const saved = savedSettingsRef.current;
    return Boolean(
      clearOpenAiApiKey ||
      nextApiKey.trim() ||
      !saved ||
      saved.locale !== nextSettings.locale ||
      saved.theme !== nextSettings.theme ||
      saved.openAiModel !== nextSettings.openAiModel,
    );
  }

  async function save(clearOpenAiApiKey = false) {
    const nextSettings = settingsRef.current;
    if (!nextSettings) return false;
    return enqueueSave(nextSettings, apiKeyRef.current, clearOpenAiApiKey, { skipUnchanged: false });
  }

  function enqueueSave(
    nextSettings: Settings,
    nextApiKey: string,
    clearOpenAiApiKey = false,
    options: { skipUnchanged?: boolean } = {},
  ) {
    if (options.skipUnchanged && !hasUnsavedChanges(nextSettings, nextApiKey, clearOpenAiApiKey)) {
      return Promise.resolve(true);
    }

    pendingSaveCountRef.current += 1;
    setSaving(true);
    setError("");
    setMessage("");

    const saveTask = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const updated = await api.updateSettings({
            locale: nextSettings.locale,
            theme: nextSettings.theme,
            openAiModel: nextSettings.openAiModel,
            ...(nextApiKey.trim() ? { openAiApiKey: nextApiKey.trim() } : {}),
            ...(clearOpenAiApiKey ? { clearOpenAiApiKey: true } : {}),
          });
          savedSettingsRef.current = persistedSettings(updated);
          const latestSettings = { ...updated, ...persistedSettings(settingsRef.current ?? updated) };
          setDraftSettings(latestSettings);
          setLocale(latestSettings.locale);
          if (nextApiKey.trim() || clearOpenAiApiKey) setDraftApiKey("");
          setMessage(clearOpenAiApiKey ? __("ลบ OpenAI API key แล้ว") : __("บันทึกการตั้งค่าแล้ว"));
          return true;
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : __("บันทึกการตั้งค่าไม่สำเร็จ"));
          return false;
        } finally {
          pendingSaveCountRef.current -= 1;
          if (pendingSaveCountRef.current === 0) setSaving(false);
        }
      });

    saveQueueRef.current = saveTask.then(() => undefined);
    return saveTask;
  }

  const autoSaveOnBlur = () => {
    const nextSettings = settingsRef.current;
    if (!nextSettings) return;
    void enqueueSave(nextSettings, apiKeyRef.current, false, { skipUnchanged: true });
  };

  const updateAndSave = (patch: Partial<PersistedSettings>) => {
    const current = settingsRef.current;
    if (!current) return;
    const nextSettings = { ...current, ...patch };
    setDraftSettings(nextSettings);
    if (patch.locale) setLocale(patch.locale);
    void enqueueSave(nextSettings, apiKeyRef.current, false, { skipUnchanged: true });
  };

  async function testConnection() {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      if (apiKey.trim() && !(await save(false))) return;
      const result = await api.testOpenAi();
      setMessage(__("เชื่อมต่อ OpenAI สำเร็จ ({{model}})", { model: result.model }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("ทดสอบ OpenAI ไม่สำเร็จ"));
    } finally {
      setTesting(false);
    }
  }

  if (error && !settings) {
    return <PageError message={error} onRetry={() => void load()} />;
  }
  if (!settings) return <PageLoading />;

  return <Box maxWidth={720}>
    <Typography variant="h4" fontWeight={800} mb={3}>{__("Settings")}</Typography>
    {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <SectionHeader icon={TuneRounded} title={__("ทั่วไป")} description={__("ภาษาและธีมของแอป")} />
        <Stack spacing={2}>
          <GlobalInput
            select
            fullWidth
            label={__("ภาษา")}
            value={settings.locale}
            onChange={(event) => updateAndSave({
              locale: event.target.value as Settings["locale"],
            })}
          >
            <MenuItem value="th">{__("ไทย")}</MenuItem>
            <MenuItem value="en">{__("English")}</MenuItem>
          </GlobalInput>
          <GlobalInput
            select
            fullWidth
            label={__("ธีม")}
            value={settings.theme}
            onChange={(event) => updateAndSave({
              theme: event.target.value as Settings["theme"],
            })}
          >
            <MenuItem value="system">{__("ตามระบบ")}</MenuItem>
            <MenuItem value="light">{__("สว่าง")}</MenuItem>
            <MenuItem value="dark">{__("มืด")}</MenuItem>
          </GlobalInput>
        </Stack>
      </CardContent>
    </Card>
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <SectionHeader
          icon={AutoAwesomeRounded}
          title={__("AI ตรวจทานข้อความ")}
          description={__("API key จะถูกเข้ารหัสและใช้งานผ่าน backend เท่านั้น")}
        />
        <Stack spacing={2}>
          <GlobalInput
            fullWidth
            type="password"
            label={__("OpenAI API key")}
            value={apiKey}
            onChange={(event) => setDraftApiKey(event.target.value)}
            placeholder={settings.openAiConfigured
              ? __("บันทึกแล้ว {{hint}} — กรอกใหม่เมื่อต้องการเปลี่ยน", { hint: settings.openAiKeyHint ?? "" })
              : "sk-..."}
            autoComplete="new-password"
            helperText={settings.openAiConfigured
              ? __("สถานะ: ตั้งค่าแล้ว {{hint}}", { hint: settings.openAiKeyHint ?? "" })
              : __("ยังไม่ได้ตั้งค่า")}
            onBlur={autoSaveOnBlur}
          />
          <GlobalInput
            select
            fullWidth
            label={__("Model")}
            value={settings.openAiModel}
            onChange={(event) => updateAndSave({
              openAiModel: event.target.value as Settings["openAiModel"],
            })}
            helperText={__(modelDescriptions[settings.openAiModel])}
          >
            {Object.keys(modelDescriptions).map((model) => (
              <MenuItem key={model} value={model}>{model}</MenuItem>
            ))}
          </GlobalInput>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
            <Box>
              {settings.openAiConfigured && (
                <Button
                  color="error"
                  disabled={saving || testing}
                  onClick={() => void save(true)}
                >
                  {__("ลบ API key")}
                </Button>
              )}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <Button
                variant="outlined"
                disabled={saving || testing || (!settings.openAiConfigured && !apiKey.trim())}
                onClick={() => void testConnection()}
              >
                {testing ? __("กำลังทดสอบ…") : __("ทดสอบการเชื่อมต่อ")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  </Box>;
}
